import { configureStore, ThunkAction, Action, combineReducers } from '@reduxjs/toolkit';
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import userManagementReducer from 'features/user-management/reducers/userManagementSlice';
import snowflakeAccountsReducer from 'features/snowflake-accounts/reducers/snowflakeAccountsSlice';
import signupReducer from 'features/frictionless-signup/reducers/signupSlice';
import onboardingReducer from 'features/onboarding/reducers/onboardingSlice';
import scimManagementReducer from 'features/scim-management/reducers/scimManagementSlice';
import { adminApi } from './apis';

/**
 * Persist configuration for onboarding slice
 * Persists the entire onboarding state to localStorage
 */
const onboardingPersistConfig = {
  key: 'onboarding',
  version: 1,
  storage,
};

/**
 * Persist configuration for signup slice
 * Persists the entire signup state to localStorage
 */
const signupPersistConfig = {
  key: 'signup',
  version: 1,
  storage,
};

/**
 * Combine all reducers
 */
const rootReducer = combineReducers({
  userManagement: userManagementReducer,
  snowflakeAccounts: snowflakeAccountsReducer,
  signup: persistReducer(signupPersistConfig, signupReducer),
  onboarding: persistReducer(onboardingPersistConfig, onboardingReducer),
  scimManagement: scimManagementReducer,
  [adminApi.reducerPath]: adminApi.reducer,
});

/**
 * Configure the Redux store with persisted reducers
 */
export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(adminApi.middleware),
});

/**
 * Persistor for redux-persist
 * This must be used with PersistGate in the app
 */
export const persistor = persistStore(store);

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action<string>>;
